import React, { FunctionComponent, useEffect } from 'react'
import { ViewStyle } from 'react-native'

import styled, { useTheme } from 'styled-components/native'

import { getIsActiveNavItem, NavigationItem } from '@lyrahealth-inc/shared-app-logic'

import { useTabBarVisibility } from './TabBarContext'
import { BodyText, PressableOpacity } from '../../atoms'
import { AccessibilityRolesNative } from '../../constants'
import { BodyTextSize } from '../../styles'
import { ThemeType, tID } from '../../utils'

export type TabBarProps = {
  tabs: NavigationItem[]
  testID?: string
  style?: ViewStyle
  currentPathname?: string
  currentScreen?: string
  isFixedToBottom?: boolean
}

const TabsBarContainer = styled.View<{ theme: ThemeType; isFixedToBottom?: boolean }>(({ theme, isFixedToBottom }) => ({
  flexDirection: 'row',
  justifyContent: 'space-around',
  background: theme.colors.backgroundPrimary,
  ...(isFixedToBottom && {
    position: 'fixed',
    bottom: 0,
    zIndex: 1,
    width: '100%',
    boxShadow: `0px -3px 10px ${theme.colors.shadowLight}`,
  }),
}))

const TabItem = styled(PressableOpacity)<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  marginBottom: spacing['24px'],
  paddingHorizontal: spacing['16px'],
  position: 'relative',
  alignItems: 'center',
}))

const TabText = styled(BodyText)(() => ({
  marginTop: '2px',
}))

export const TabBar: FunctionComponent<TabBarProps> = ({
  tabs,
  testID = 'TabBar',
  style,
  currentPathname,
  currentScreen,
  isFixedToBottom,
}) => {
  const { colors } = useTheme()
  const { onTabBarVisibleChange, setTabBarHeight } = useTabBarVisibility()

  useEffect(() => {
    if (isFixedToBottom) {
      onTabBarVisibleChange(true, currentScreen)

      return () => {
        onTabBarVisibleChange(false, currentScreen)
      }
    }
    return
  }, [currentScreen, isFixedToBottom, onTabBarVisibleChange])

  return (
    <TabsBarContainer
      testID={tID(testID)}
      style={style}
      isFixedToBottom={isFixedToBottom}
      accessibilityRole={AccessibilityRolesNative.TAB_LIST}
      onLayout={(event) => {
        const height = event.nativeEvent.layout.height
        if (height) {
          setTabBarHeight(height)
        }
      }}
    >
      {tabs.map((tab) => {
        const isActive = getIsActiveNavItem(tab, currentPathname, currentScreen)
        return tab.condition && tab.condition() ? (
          <TabItem
            key={tab.id}
            onPress={tab.onPressItem}
            testID={tID(`${testID}-tab-${tab.id}`)}
            accessibilityRole={AccessibilityRolesNative.TAB}
            accessibilityState={{ selected: isActive }}
          >
            {tab.icon && tab.icon(isActive)}
            <TabText
              size={BodyTextSize.CAPTION}
              text={tab.text}
              color={isActive ? colors.textActive : colors.textSecondary}
            />
          </TabItem>
        ) : null
      })}
    </TabsBarContainer>
  )
}
